import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import { Loading } from "./components/wf";
import { AuthProvider, isManagerUser, useAuth } from "./lib/auth";
import { BrandColorSync } from "./lib/queries";
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";
import Jobs from "./pages/Jobs";
import WorkOrderDetail from "./pages/WorkOrderDetail";
import WorkOrderForm from "./pages/WorkOrderForm";
import EntryForm from "./pages/EntryForm";
import FloorPlan from "./pages/FloorPlan";
import { Customers, CustomerForm } from "./pages/Customers";
import { Team, EmployeeForm, KastleForm } from "./pages/Team";
import { Parts, PartForm } from "./pages/Parts";
import { Projects, ProjectDetail, TaskForm } from "./pages/Projects";
import { BudgetJobs, HourlyJobs } from "./pages/MoneyJobs";
import TeamHours from "./pages/TeamHours";
import { Documentation, DocForm } from "./pages/Documentation";
import Profile from "./pages/Profile";
import { Settings, SettingsPage } from "./pages/Settings";
import FreeCodes from "./pages/FreeCodes";

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true, staleTime: 10_000 } } });

function Protected({ manager, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (manager && !isManagerUser(user)) return <Navigate to="/" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandColorSync />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route element={<Protected />}>
              <Route element={<Layout />}>
                <Route path="/" element={<Jobs />} />
                <Route path="/completed-jobs" element={<Jobs completed />} />
                <Route path="/work-orders/new" element={<WorkOrderForm />} />
                <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
                <Route path="/work-orders/:id/edit" element={<WorkOrderForm />} />
                <Route path="/work-orders/:wid/floor-plan" element={<FloorPlan />} />
                <Route path="/work-orders/:wid/entry/:kind" element={<EntryForm />} />
                <Route path="/work-orders/:wid/entry/:kind/:eid" element={<EntryForm />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/new" element={<CustomerForm />} />
                <Route path="/customers/:id" element={<CustomerForm />} />
                <Route path="/team" element={<Team />} />
                <Route path="/employees/new" element={<EmployeeForm />} />
                <Route path="/employees/:id" element={<EmployeeForm />} />
                <Route path="/kastle/new" element={<KastleForm />} />
                <Route path="/kastle/:id" element={<KastleForm />} />
                <Route path="/parts" element={<Parts />} />
                <Route path="/parts/new" element={<PartForm />} />
                <Route path="/parts/:id" element={<PartForm />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/projects/:pid/tasks/new" element={<TaskForm />} />
                <Route path="/projects/:pid/tasks/:tid" element={<TaskForm />} />
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/documentation/new" element={<DocForm />} />
                <Route path="/documentation/:id" element={<DocForm />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/free-codes" element={<FreeCodes />} />
                <Route element={<Protected manager />}>
                  <Route path="/team-hours" element={<TeamHours />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/:page" element={<SettingsPage />} />
                </Route>
                <Route element={<Protected admin />}>
                  <Route path="/budget-jobs" element={<BudgetJobs />} />
                  <Route path="/hourly-jobs" element={<HourlyJobs />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </QueryClientProvider>
  );
}
